import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "社区新闻", to: "/news" },
  { label: "邻里论坛", to: "/forum" },
  { label: "生活服务", to: "/services" },
  { label: "关于邻居", to: "/about" },
  { label: "联系我们", to: "/contact" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setKeyword("");
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-background-50/95 backdrop-blur border-b border-background-200">
      <div className="w-full px-4 md:px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0 group">
          <span className="w-9 h-9 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center">
            <i className="ri-home-heart-fill text-lg"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="text-lg font-bold text-foreground-950 tracking-wide">邻居</span>
            <span className="text-[10px] text-foreground-500 font-label">本地社区交流平台</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 flex-1 justify-center">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `px-3 py-2 rounded-md text-sm transition-colors whitespace-nowrap ${
                  isActive
                    ? "text-primary-600 font-semibold bg-primary-50"
                    : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <form onSubmit={handleSearch} className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索新闻 / 帖子 / 服务"
              className="w-52 xl:w-60 h-9 pl-9 pr-3 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:bg-background-50"
            />
          </form>
          <Link
            to="/post"
            className="inline-flex items-center gap-1 h-9 px-4 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            发布信息
          </Link>
        </div>

        <div className="flex lg:hidden items-center gap-2">
          <Link
            to="/post"
            className="inline-flex items-center h-9 px-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium"
          >
            <i className="ri-add-line"></i>
          </Link>
          <button
            type="button"
            aria-label="打开菜单"
            onClick={() => setMobileOpen((v) => !v)}
            className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-700"
          >
            <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50 px-4 py-3">
          <form onSubmit={handleSearch} className="relative mb-3">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索新闻 / 帖子 / 服务"
              className="w-full h-10 pl-9 pr-3 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
            />
          </form>
          <nav className="grid grid-cols-2 gap-2">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `px-3 py-2.5 rounded-md text-sm ${
                    isActive ? "text-primary-600 bg-primary-50 font-semibold" : "text-foreground-700 bg-background-100"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <NavLink
              to="/post"
              className="px-3 py-2.5 rounded-md text-sm bg-primary-500 text-background-50 font-medium flex items-center gap-1 col-span-2 justify-center"
            >
              <i className="ri-add-line"></i> 发布信息
            </NavLink>
          </nav>
        </div>
      )}
    </header>
  );
}